import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navigation/Navbar';
import SciFiBackground from '../components/SciFiBackground/SciFiBackground';
import PaymentOptions from '../components/Payment/PaymentOptions';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  CardMedia,
  useTheme,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { apiClient } from '../utils/apiClient';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const Payment = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { productId: urlProductId } = useParams();
  const productId = urlProductId || query.get('productId');
  const initialQuantity = parseInt(query.get('quantity'), 10) || 1;
  const { user, token } = useAuth();
  const toast = useToast();
  
  const [quantity, setQuantity] = useState(initialQuantity);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('mixed'); // Default to mixed for optimal payment strategy
  const [paymentBreakdown, setPaymentBreakdown] = useState({
    recoveryWallet: 0,
    mainWallet: 0,
    onlinePayment: 0
  });
  const [form, setForm] = useState({ 
    name: user?.name || '', 
    card: '', 
    expiry: '', 
    cvc: '',
    email: user?.email || '',
    address: '',
    city: '',
    postalCode: ''
  });
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const borderRadius = '25px';
  const compactRadius = '15px';
  
  const glassPanelStyles = {
    p: { xs: 3, md: 4 },
    height: '100%',
    borderRadius,
    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
    border: '1px solid',
    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
    backdropFilter: 'blur(24px)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 30px 80px rgba(0, 0, 0, 0.45)'
        : '0 35px 80px rgba(15, 23, 42, 0.12)',
    color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
  };
  const subtleSurface = theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)';
  const headingColor = theme.palette.mode === 'dark' ? '#ffffff' : '#000000';
  const mutedColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      
      if (!productId) {
        setError(t('payment.noProductId'));
        setLoading(false);
        return;
      }
      
      try {
        const data = await apiClient.get(`/api/products/${productId}`);
        
        if (data?.success && data.data) {
          setProduct(data.data);
        } else {
          throw new Error(data?.message || 'Product not found');
        }
      } catch (error) {
        console.error('❌ Error fetching product:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId, token]);

  const formatPrice = (price) => `${new Intl.NumberFormat('en-US').format(price ?? 0)} TND`;

  const formatCardNumber = (value) => {
    // Remove all non-numeric characters
    const numericValue = value.replace(/\D/g, '');
    // Add spaces every 4 digits
    return numericValue.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  const formatExpiry = (value) => {
    // Remove all non-numeric characters
    const numericValue = value.replace(/\D/g, '');
    // Add slash after 2 digits
    if (numericValue.length >= 2) {
      return numericValue.substring(0, 2) + '/' + numericValue.substring(2, 4);
    }
    return numericValue;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let formattedValue = value;
    if (name === 'card') {
      formattedValue = formatCardNumber(value);
    } else if (name === 'expiry') {
      formattedValue = formatExpiry(value);
    } else if (name === 'cvc') {
      formattedValue = value.replace(/\D/g, '').substring(0, 4);
    }
    
    setForm(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  };

  const validateForm = () => {
    const errors = [];
    
    if (!form.name.trim()) errors.push(t('payment.nameRequired'));
    if (!form.email.trim()) errors.push(t('payment.emailRequired'));
    if (!form.card.replace(/\s/g, '')) errors.push(t('payment.cardRequired'));
    if (!form.expiry) errors.push(t('payment.expiryRequired'));
    if (!form.cvc) errors.push(t('payment.cvcRequired'));
    if (!form.address.trim()) errors.push(t('payment.addressRequired'));
    if (!form.city.trim()) errors.push(t('payment.cityRequired'));
    if (!form.postalCode.trim()) errors.push(t('payment.postalCodeRequired'));
    
    // Validate card number length (basic validation)
    const cardNumber = form.card.replace(/\s/g, '');
    if (cardNumber.length < 13 || cardNumber.length > 19) {
      errors.push(t('payment.invalidCardLength'));
    }
    
    // Validate expiry format
    if (!/^\d{2}\/\d{2}$/.test(form.expiry)) {
      errors.push(t('payment.invalidExpiryFormat'));
    }
    
    // Validate CVC length
    if (form.cvc.length < 3 || form.cvc.length > 4) {
      errors.push(t('payment.invalidCvcLength'));
    }
    
    return errors;
  };

  const handlePay = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.warning(t('payment.loginRequired'));
      navigate('/login', { state: { from: `/payment/${product?._id || ''}?quantity=${quantity}` } });
      return;
    }

    // Validate based on payment method
    if (paymentMethod === 'online' || paymentMethod === 'PayPal' || 
        (paymentMethod === 'mixed' && paymentBreakdown.onlinePayment > 0)) {
      const validationErrors = validateForm();
      if (validationErrors.length > 0) {
        toast.error(t('payment.fixErrors') + '\n' + validationErrors.join('\n'));
        return;
      }
    }
    
    setPaying(true);
    
    try {

      let requestPayload = {
        quantity,
        paymentMethod,
      };

      // Add payment metadata for online payments
      if (paymentMethod === 'online' || paymentMethod === 'PayPal' || 
          (paymentMethod === 'mixed' && paymentBreakdown.onlinePayment > 0)) {
        requestPayload.paymentMeta = {
          last4: form.card.replace(/\s/g, '').slice(-4),
          expiry: form.expiry,
          email: form.email,
          shippingCity: form.city,
          shippingPostalCode: form.postalCode
        };
      }

      // Add payment breakdown for mixed payments
      if (paymentMethod === 'mixed') {
        requestPayload.paymentBreakdown = paymentBreakdown;
      }

      // Call backend purchase endpoint
      const result = await apiClient.post(`/api/products/${product._id}/purchase`, requestPayload);

      if (!result?.success) {
        throw new Error(result?.message || t('payment.purchaseFailed'));
      }

      if (result?.success && result?.data?.product) {
        // Update local product with new stock
        setProduct(result.data.product);
        setSuccess(true);
        // Redirect after a short delay
        setTimeout(() => {
          navigate('/ecommerce', {
            state: {
              message: t('payment.paymentSuccessRedirect', { name: product.name })
            }
          });
        }, 3000);
      } else {
        throw new Error(t('payment.unexpectedResponse'));
      }
      
    } catch (error) {
  console.error('❌ Payment failed:', error);
  toast.error(error.message || t('payment.paymentFailed'));
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <SciFiBackground>
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Navbar />
          <Container maxWidth="md" sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <Paper
              sx={{
                p: 8,
                textAlign: 'center',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
                backdropFilter: 'blur(20px)',
                borderRadius,
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
              }}
            >
              <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main', mb: 4 }} />
              <Typography variant="h6" sx={{ color: mutedColor }}>
                {t('payment.loadingProduct')}
              </Typography>
            </Paper>
          </Container>
        </Box>
      </SciFiBackground>
    );
  }

  if (error) {
    return (
      <SciFiBackground>
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Navbar />
          <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <Paper
              sx={{
                p: 6,
                textAlign: 'center',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
                backdropFilter: 'blur(20px)',
                borderRadius,
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
              }}
            >
              <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: headingColor }}>
                {t('payment.productNotFoundTitle')}
              </Typography>
              <Typography paragraph sx={{ color: mutedColor }}>
                {error}
              </Typography>
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/ecommerce')}
                sx={{ mt: 2, borderRadius: compactRadius, textTransform: 'none', fontWeight: 600 }}
              >
                {t('payment.backToProducts')}
              </Button>
            </Paper>
          </Container>
        </Box>
      </SciFiBackground>
    );
  }

  if (success) {
    return (
      <SciFiBackground>
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Navbar />
          <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <Paper
              sx={{
                p: 6,
                textAlign: 'center',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
                backdropFilter: 'blur(20px)',
                borderRadius,
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: headingColor }}>
                {t('payment.paymentSuccess')}
              </Typography>
              <Typography paragraph sx={{ color: mutedColor }}>
                {t('payment.orderProcessed', { name: product.name })}
              </Typography>
              
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 4,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
                  border: '1px solid',
                  borderColor: 'success.light',
                  borderRadius: compactRadius,
                }}
              >
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: mutedColor }}>{t('payment.totalPaid')}:</Typography>
                    <Typography fontWeight="bold" color="success.main">
                      {formatPrice(product.price * quantity)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: mutedColor }}>{t('payment.quantity')}:</Typography>
                    <Typography fontWeight="bold" color="success.main">
                      {quantity}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
              
              <Typography variant="caption" sx={{ color: mutedColor }}>
                {t('payment.redirecting')}
              </Typography>
            </Paper>
          </Container>
        </Box>
      </SciFiBackground>
    );
  }

  return (
    <SciFiBackground>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'transparent',
          color: 'text.primary',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Navbar />
        <Container
          maxWidth="lg"
          sx={{
            flex: 1,
            width: '100%',
            pt: { xs: 4, md: 7 },
            pb: 6,
          }}
        >
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {/* Product Summary */}
            <Grid item xs={12} lg={6}>
              <Paper sx={glassPanelStyles}>
                <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                  <ShoppingBagIcon color="primary" />
                  <Typography variant="h5" fontWeight={700} color={headingColor}>
                    {t('payment.orderSummary')}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={3} mb={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      width: 110,
                      height: 110,
                      overflow: 'hidden',
                      borderRadius: compactRadius,
                      bgcolor: subtleSurface,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {product.images?.length > 0 ? (
                      <CardMedia
                        component="img"
                        image={product.images[0].thumbnailUrl}
                        alt={product.name}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <ShoppingBagIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                    )}
                  </Paper>
                  
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight={600} gutterBottom color={headingColor}>
                      {product.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: mutedColor, mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {product.description}
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" color="primary.main" fontWeight={700}>
                        {formatPrice(product.price)}
                      </Typography>
                      {product.soldOut && (
                        <Chip label={t('payment.soldOut')} color="error" size="small" />
                      )}
                    </Stack>
                  </Box>
                </Stack>

                <Divider sx={{ my: 3 }} />

                <Stack spacing={3}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ color: mutedColor }}>{t('payment.quantity')}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <IconButton 
                        size="small" 
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        disabled={quantity <= 1}
                        sx={{ bgcolor: subtleSurface }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography fontWeight={600} sx={{ minWidth: 30, textAlign: 'center' }}>
                        {quantity}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => setQuantity(quantity + 1)}
                        sx={{ bgcolor: subtleSurface }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>

                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: mutedColor }}>{t('payment.subtotal')}</Typography>
                      <Typography fontWeight={600}>{formatPrice(product.price * quantity)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: mutedColor }}>{t('payment.shipping')}</Typography>
                      <Typography color="success.main" fontWeight={600}>{t('payment.free')}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: mutedColor }}>{t('payment.tax')}</Typography>
                      <Typography fontWeight={600}>{formatPrice((product.price * quantity) * 0.2)}</Typography>
                    </Stack>
                  </Stack>

                  <Divider />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={700} color={headingColor}>{t('payment.total')}</Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                      {formatPrice((product.price * quantity) * 1.2)}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            {/* Payment Form */}
            <Grid item xs={12} lg={6}>
              <Paper sx={glassPanelStyles}>
                <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                  <ReceiptIcon color="secondary" />
                  <Typography variant="h5" fontWeight={700} color={headingColor}>
                    {t('payment.paymentInfo')}
                  </Typography>
                </Stack>

                <PaymentOptions
                  totalAmount={product.price * quantity}
                  onPaymentMethodChange={setPaymentMethod}
                  selectedMethod={paymentMethod}
                  onPaymentBreakdownChange={setPaymentBreakdown}
                />

                {/* Traditional Payment Form */}
                {(paymentMethod === 'online' || paymentMethod === 'PayPal' || 
                  (paymentMethod === 'mixed' && paymentBreakdown.onlinePayment > 0)) && (
                  <Box component="form" onSubmit={handlePay} sx={{ mt: 4 }}>
                    <Divider sx={{ mb: 3 }}>
                      <Chip icon={<CreditCardIcon />} label={t('payment.creditCardDetails')} />
                    </Divider>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('payment.fullName')}
                          name="name"
                          value={form.name}
                          onChange={handleInputChange}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('email')}
                          name="email"
                          type="email"
                          value={form.email}
                          onChange={handleInputChange}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t('payment.cardNumber')}
                          name="card"
                          value={form.card}
                          onChange={handleInputChange}
                          required
                          variant="outlined"
                          inputProps={{ maxLength: 19 }}
                          placeholder="0000 0000 0000 0000"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={t('payment.expiryDate')}
                          name="expiry"
                          value={form.expiry}
                          onChange={handleInputChange}
                          required
                          variant="outlined"
                          placeholder="MM/YY"
                          inputProps={{ maxLength: 5 }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={t('payment.cvc')}
                          name="cvc"
                          value={form.cvc}
                          onChange={handleInputChange}
                          required
                          variant="outlined"
                          placeholder="123"
                          inputProps={{ maxLength: 4 }}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalShippingIcon fontSize="small" /> {t('payment.billingAddress')}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t('payment.address')}
                          name="address"
                          value={form.address}
                          onChange={handleInputChange}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={t('payment.city')}
                          name="city"
                          value={form.city}
                          onChange={handleInputChange}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={t('payment.postalCode')}
                          name="postalCode"
                          value={form.postalCode}
                          onChange={handleInputChange}
                          required
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>

                    <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => navigate('/ecommerce')}
                        sx={{ 
                          color: mutedColor, 
                          borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                          borderRadius: compactRadius,
                          textTransform: 'none',
                          fontWeight: 600
                        }}
                      >
                        {t('cancel')}
                      </Button>
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        disabled={paying}
                        sx={{
                          background: 'linear-gradient(45deg, #22c55e, #16a34a)',
                          boxShadow: '0 4px 14px 0 rgba(34, 197, 94, 0.39)',
                          borderRadius: compactRadius,
                          textTransform: 'none',
                          fontWeight: 600
                        }}
                      >
                        {paying ? <CircularProgress size={24} color="inherit" /> : t('payment.payAmount', { amount: formatPrice((product.price * quantity) * 1.2) })}
                      </Button>
                    </Stack>
                  </Box>
                )}

                {/* Wallet Payment Buttons */}
                {(paymentMethod === 'tokens' || paymentMethod === 'recovery_wallet') && (
                  <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => navigate('/ecommerce')}
                      sx={{ 
                        color: mutedColor, 
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                        borderRadius: compactRadius,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      {t('cancel')}
                    </Button>
                    <Button
                      fullWidth
                      onClick={handlePay}
                      disabled={paying}
                      variant="contained"
                      sx={{
                          background: 'linear-gradient(105deg, #7657ff, #5b3ee0)',
                        boxShadow: '0 4px 14px 0 rgba(139, 92, 246, 0.39)',
                        borderRadius: compactRadius,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      {paying ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        t('payment.purchaseWithWallet', { wallet: paymentMethod === 'recovery_wallet' ? t('payment.recoveryWallet') : t('payment.mainWallet') })
                      )}
                    </Button>
                  </Stack>
                )}

                {/* Mixed Payment Button */}
                {paymentMethod === 'mixed' && paymentBreakdown.onlinePayment === 0 && (
                  <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => navigate('/ecommerce')}
                      sx={{ 
                        color: mutedColor, 
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                        borderRadius: compactRadius,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      {t('cancel')}
                    </Button>
                    <Button
                      fullWidth
                      onClick={handlePay}
                      disabled={paying}
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(105deg, #7657ff, #ff714f)',
                        boxShadow: '0 4px 14px 0 rgba(139, 92, 246, 0.39)',
                        borderRadius: compactRadius,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      {paying ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        t('payment.completePurchaseAuto')
                      )}
                    </Button>
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </SciFiBackground>
  );
};

export default Payment;
